import {
  Autocomplete,
  Box,
  FormHelperText,
  InputAdornment,
  InputLabel,
  SxProps,
  TextField,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React, { useState } from "react";
import { isTruthy } from "helpers/methods";
import CustomAutoCompleteStyles from "global/components/CustomAutoComplete/CustomAutoComplete.styles";

export interface CustomProps {
  options: any;
  sx?: SxProps;
  id: string;
  placeHolder?: string;
  onChange: Function;
  onChangeForTextField?: Function;
  errorMessage?: string;
  value: any;
  disabled?: boolean;
  name?: string;
  ref?: any;
  loading?: boolean;
  clearIcon?: boolean;
  filterOptions?: any;
  type?: string;
  inputValue?: string;
  multiple?: boolean;
  groupBy?: (option: any) => string;
  getOptionLabel?: (option: any) => string;
  renderTags?: (value: string[], getTagProps: any) => React.ReactNode;
  renderOption?: (props: any, option: any, state: any) => React.ReactNode;
  freeSolo?: boolean;
  textFieldVariant?: string;
  label?: string;
  /** Accessible name for fields the design leaves visually unlabelled. */
  ariaLabel?: string;
  required?: boolean;
  PaperComponent?: any;
  onBlur?: Function;
  error?: any;
  maxLength?: number;
  /** When true, shows a chevron on the right while the field is not focused; MUI popup icon shows while focused. */
  showDropdownIconWhenBlurred?: boolean;
  disableClearable?: boolean;
  /** Forwarded to MUI Autocomplete (e.g. paper minWidth for the options list). */
  componentsProps?: Record<string, any>;
  /**
   * Id(s) of element(s) that describe the field (hint text rendered by the
   * consumer). Merged with the ids this component owns for its own error text.
   */
  ariaDescribedBy?: string;
  /** When `freeSolo` is true, defaults to true. Forced false when `freeSolo` is false. */
  selectOnFocus?: boolean;
}

const CustomAutoComplete = (props: CustomProps) => {
  const classes = CustomAutoCompleteStyles;
  const { error = null } = props;
  const [inputFocused, setInputFocused] = useState(false);
  const showBlurChevron =
    Boolean(props.showDropdownIconWhenBlurred) && !inputFocused;
  const freeSolo = props.freeSolo ?? true;
  const disableClearable = props.disableClearable ?? false;
  const selectOnFocus =
    freeSolo === false ? false : (props.selectOnFocus ?? true);
  const errorId = `${props.id}-error`;

  // MUI's `TextField` derives this id from the `id` it receives via `params`.
  const muiHelperTextId = `${props.id}-helper-text`;
  const errorMessageId = `${props.id}-error`;
  const describedBy =
    [
      props.ariaDescribedBy,
      error ? muiHelperTextId : undefined,
      props.errorMessage ? errorMessageId : undefined,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <Box width="100%">
      {props.label && (
        <Box mb={1}>
          <InputLabel
            id={`${props.id}-label`}
            htmlFor={props.id}
            sx={classes.inputLabel}
            required={props.required}
          >
            {props.label}
          </InputLabel>
        </Box>
      )}
      <Autocomplete
        multiple={props.multiple}
        id={props.id}
        freeSolo={freeSolo}
        disableClearable={disableClearable}
        componentsProps={props.componentsProps}
        componentName={props.name}
        inputValue={props.inputValue}
        loading={props?.loading}
        sx={props.sx ? props.sx : classes.autoComplete}
        value={props.value}
        clearIcon={props.clearIcon}
        options={props.options || []}
        disabled={props.disabled}
        filterOptions={props.filterOptions}
        onChange={(event: any, newValue: any, index: any) =>
          props.onChange(event, newValue, index)
        }
        PaperComponent={props.PaperComponent}
        forcePopupIcon={
          props.showDropdownIconWhenBlurred ? inputFocused : true
        }
        fullWidth
        renderInput={(params: any) => (
          <TextField
            ref={props?.ref}
            {...params}
            inputProps={{
              ...params.inputProps,
              maxLength: props.maxLength ?? 256,
              ...(!props.label && props.ariaLabel
                ? { "aria-label": props.ariaLabel }
                : {}),
              ...(isTruthy(props.errorMessage) && {
                "aria-describedby": errorId,
              }),
              ...(freeSolo === false && { readOnly: true }),
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                if (props.showDropdownIconWhenBlurred) {
                  setInputFocused(false);
                }
                params.inputProps?.onBlur?.(e);
                props.onBlur?.(e);
              },
            }}
            variant={
              props.textFieldVariant ? props.textFieldVariant : "standard"
            }
            placeholder={props.placeHolder}
            error={isTruthy(props.errorMessage)}
            fullWidth
            onChange={(event: any) =>
              props.onChangeForTextField && props.onChangeForTextField(event)
            }
            sx={{
              ...classes.textField,
              ...(freeSolo === false && {
                "& .MuiInputBase-input": { cursor: "pointer" },
              }),
              "& .MuiInputBase-root:hover:not(.Mui-disabled, .Mui-error):before":
                {
                  borderBottom: "none",
                },
              "& .MuiInputBase-root.Mui-disabled:before": {
                borderBottomStyle: "none",
              },
            }}
            onKeyDown={(event: any) => {
              if (
                event.key === "Backspace" ||
                event.key === "Delete" ||
                event.key === "ArrowLeft" ||
                event.key === "ArrowRight" ||
                event.key === "Home" ||
                event.key === "End"
              ) {
                event.stopPropagation();
              }
            }}
            {...(error && { error: true, helperText: error })}
            InputProps={
              props.showDropdownIconWhenBlurred
                ? {
                    ...params.InputProps,
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                      setInputFocused(true);
                      params.InputProps.onFocus?.(e);
                    },
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        {showBlurChevron && (
                          <InputAdornment
                            position="end"
                            sx={{ pointerEvents: "none", mr: 0.5 }}
                          >
                            <KeyboardArrowDownIcon
                              fontSize="small"
                              sx={{ color: "text.secondary", opacity: 0.8 }}
                            />
                          </InputAdornment>
                        )}
                      </>
                    ),
                  }
                : params.InputProps
            }
          />
        )}
        getOptionLabel={props.getOptionLabel}
        groupBy={props.groupBy}
        renderTags={props.renderTags}
        renderOption={props.renderOption}
        selectOnFocus={selectOnFocus}
        clearOnBlur={false}
        handleHomeEndKeys
      />
      {props.errorMessage && (
        // Validation runs on submit, with focus still on the submit button, so
        // `aria-describedby` alone never reaches the user — the message has to
        // announce itself as it appears (WCAG 3.3.1 / 4.1.3).
        <FormHelperText id={errorMessageId} role="alert" error sx={{ ml: 2 }}>
          {props.errorMessage}
        </FormHelperText>
      )}
    </Box>
  );
};

export default CustomAutoComplete;
